import { FilterOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import type { ViewConfigTreeNode } from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { addModelIdProperty, useBooleanState } from '@salescore/frontend-common'
import { App, type ModalFuncProps, Space, Switch } from 'antd'
import { t } from 'i18next'
import { type ReactElement, useEffect } from 'react'
import { useRecoilValue } from 'recoil'

import { changesAtom } from '../../../../../recoil/recordsHistory'
import { useGetModel } from '../../../../../recoil/view/selectors/connectionsSelector'
import { useEditNodeMutation, useViewConfigSheet } from '../../../../../recoil/viewConfig'
import { applyAdvancedSettingsFlag, ViewFilterTreeForm } from './ViewFilterTreeForm'

export const buildViewJoinFiltersSettingModalProperties = ({
  node,
  onAfterFinish,
  setHasChangeToViewSchema,
}: {
  node: ViewConfigTreeNode
  onAfterFinish: () => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  title: null,
  width: '80%',
  cancelText: t('閉じる'),
  okButtonProps: { style: { display: 'none' } },
  style: { top: '3%', maxWidth: 1000 },
  footer: null,
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  content: (
    <ModalContent node={node} onAfterFinish={onAfterFinish} setHasChangeToViewSchema={setHasChangeToViewSchema} />
  ),
})

const ModalContent = ({
  node,
  onAfterFinish,
  setHasChangeToViewSchema,
}: {
  node: ViewConfigTreeNode
  onAfterFinish: () => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { editNode } = {
    editNode: useEditNodeMutation(),
  }
  const config = useViewConfigSheet()
  const getModel = useGetModel()
  const changes = useRecoilValue(changesAtom)
  const { message } = App.useApp()
  // eslint-disable-next-line sonarjs/deprecation
  const isAdvancedModeState = useBooleanState(
    (node.additionalJoinOn?.children ?? []).length > 0 || node.additionalJoinOn?.logicalOperator === 'or',
  )
  const label = node.ui?.label ?? getModel(node.modelName)?.label ?? node.modelName
  const rootNode = config.tree
  const rootNodeLabel = rootNode === undefined ? undefined : (rootNode.ui?.label ?? getModel(rootNode.modelName)?.label)
  const model = getModel(node.modelName)
  const propertiesWithNode =
    model?.properties.map((property) => ({
      node,
      model,
      property,
    })) ?? []
  const propertiesAndModelIdWithNode = addModelIdProperty({ node, model }, propertiesWithNode)
  useEffect(() => {
    if (changes.length > 0) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [changes.length, message])

  return (
    <PageHeader
      title={
        <Space>
          <FilterOutlined />
          <span>
            「{label}」{t(`ブロックの絞り込み`)}
          </span>
        </Space>
      }
      extra={[
        <Space key="advanced">
          {t(`高度なフィルタ条件`)}
          <Switch
            checked={isAdvancedModeState.isTrue}
            onChange={(value) => {
              if (value) {
                isAdvancedModeState.setTrue()
              } else {
                isAdvancedModeState.setFalse()
              }
            }}
          />
        </Space>,
      ]}
    >
      <div className="mb-4 text-xs text-gray-700">
        <p>※{t(`高度な設定項目です。通常は設定する必要はありません。`)}</p>
        {t(`絞り込みを実行した結果、「{{label}}」の行が見つからなかったとき、`, { label })}
        <br />
        {t(`・(通常の絞り込み) 「{{label}}」「{{rootNodeLabel}}」の行が両方とも表示されません。`, {
          label,
          rootNodeLabel,
        })}
        <br />
        {t(`・(ブロックの絞り込み) 「{{label}}」の行は表示されませんが、「{{rootNodeLabel}}」の行は表示されます。`, {
          label,
          rootNodeLabel,
        })}
      </div>
      <ViewFilterTreeForm
        isAdvancedMode={isAdvancedModeState.isTrue}
        filterTree={node.additionalJoinOn}
        propertiesWithNode={propertiesAndModelIdWithNode}
        onAfterFinish={(filterTree) => {
          editNode({
            node,
            f: (node) => {
              // eslint-disable-next-line no-param-reassign
              node.additionalJoinOn = applyAdvancedSettingsFlag(isAdvancedModeState.isTrue, filterTree)
            },
            onSetConfig: () => {
              setHasChangeToViewSchema(true)
            },
          })
          onAfterFinish()
        }}
      />
    </PageHeader>
  )
}
